/**
 * Service Impact Analysis Route
 *
 * Displays impact analysis showing the blast radius of a service
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useService } from '../../graphql/queries';
import ImpactAnalysis from '../../components/services/ImpactAnalysis';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { EmptyState } from '../../components/shared/EmptyState';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { ServiceDependency } from '../../graphql/types';
import '@tylertech/forge/icon';
import './$serviceId.impact.css';

export const Route = createFileRoute('/services/$serviceId/impact')({
  component: ServiceImpactPage,
});

/**
 * GraphQL query to get all dependencies in the system
 */
const GET_ALL_DEPENDENCIES = gql`
  query GetAllDependencies {
    allDependencies {
      fromServiceId
      toServiceId
      metadata {
        dependencyType
        communicationPattern
        criticality
        notes
      }
      fromService {
        entityId
        name
        shortName
        serviceType
        tier
        lifecycleStatus
      }
      toService {
        entityId
        name
        shortName
        serviceType
        tier
        lifecycleStatus
      }
    }
  }
`;

function ServiceImpactPage(): JSX.Element {
  const { serviceId } = Route.useParams();

  // Fetch service data
  const { data: serviceData, loading: serviceLoading, error: serviceError } = useService(serviceId);

  // Fetch all dependencies
  const {
    data: dependenciesData,
    loading: dependenciesLoading,
    error: dependenciesError,
  } = useQuery<{ allDependencies: ServiceDependency[] }>(GET_ALL_DEPENDENCIES);

  const loading = serviceLoading || dependenciesLoading;
  const error = serviceError || dependenciesError;

  // Loading state
  if (loading && !serviceData) {
    return (
      <div className="service-impact-page">
        <LoadingSpinner message="Loading impact analysis..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="service-impact-page">
        <EmptyState
          title="Failed to load impact analysis"
          description={(error as Error).message || 'An error occurred while loading the data'}
          icon="error"
        />
      </div>
    );
  }

  // Service not found
  if (!serviceData?.service) {
    return (
      <div className="service-impact-page">
        <EmptyState
          title="Service not found"
          description={`Service with ID ${serviceId} does not exist`}
          icon="error"
        />
      </div>
    );
  }

  const service = serviceData.service;
  const allDependencies = dependenciesData?.allDependencies || [];

  return (
    <div className="service-impact-page">
      {/* Header */}
      <div className="impact-page-header">
        <div className="impact-breadcrumb">
          <Link to="/services/$serviceId" params={{ serviceId }} className="impact-breadcrumb-link">
            ← Back to Service
          </Link>
        </div>
        <div className="impact-page-title">
          <h1>{service.name} - Impact Analysis</h1>
          <p className="impact-page-subtitle">
            Analyze the blast radius and cascading effects of this service
          </p>
        </div>
      </div>

      {/* Impact Analysis Component */}
      <ImpactAnalysis
        service={service}
        allDependencies={allDependencies}
        loading={loading}
        error={error ? (error as Error).message : null}
      />
    </div>
  );
}
